import { select, spawn, takeEvery, takeLatest, put, all, putResolve, delay, call } from 'typed-redux-saga/macro';
import { LOGGED_OUT, REGISTER_USER_SUCCESS, USER_RESUME_APP } from 'actions/types';
import { FETCH_APP_DATA } from 'actions/refresh';
import { selectIsGBUser, selectUser } from 'reducers/selectors';
import { navigateToTab } from 'hooks/navigation/useTabNavigation';
import transactionSagas from 'features/transactions/sagas';
import { getPotsAccount } from 'features/pots/actions';
import { getVRPConsents } from 'features/pre-approved-transfers/actions';
import { clearOnboardingHistory } from 'features/wealth/utils/onboardingHistory';
import { isAppInstalledAndroid, isAppInstalledIOS } from 'utils/linking';
import Sentry from 'utils/sentry';
import { isIOS, isAndroid } from '../../../constants';
import { getActivities, getConnectedAccount, getLatestPrices, getPendingTopups, getPositions, getTradingAccounts, updateWealthAccountsComplete, updateWealthAccountsWithRetry, setHmrcAppInstalledAction, } from '../actions';
import { HMRC_APP_URL_SCHEME, HMRC_APP_ANDROID_PACKAGE_NAME } from '../utils';
import autoinvestSaga from './autoinvestSaga';
import topupSaga from './topupSaga';
import withdrawSagas from './withdrawSagas';
import { UPDATE_WEALTH_ACCOUNTS } from '../actions/types';
function* handleInvestInvite() {
    const user = yield* select(selectUser);
    const isGB = user.guessedHomeCountry === 'GB';
    if (isGB) {
        navigateToTab('Invest');
    }
}
/**
 * fetching the latest prices for all the symbols that we have in pending stock alerts
 */
function* fetchPricesForPriceAlertSymbols(action) {
    const assetIdSet = new Set();
    action.payload.alerts.forEach((alert) => {
        assetIdSet.add(alert.assetId);
    });
    const assetIds = [...assetIdSet];
    if (assetIds.length) {
        yield* put(getLatestPrices(assetIds));
    }
}
function* updateWealthAccountsWorker(action) {
    const isGBUser = yield* select(selectIsGBUser);
    if (isGBUser) {
        yield* all([putResolve(getTradingAccounts()), putResolve(getConnectedAccount()), putResolve(getPotsAccount())]);
        const tradingAccounts = yield* select((state) => state.invest.accounts);
        const potsAccount = yield* select((state) => state.pots.account);
        const isPotsAccountActive = potsAccount?.status === 'ACTIVE';
        if (isPotsAccountActive) {
            if ('fetchPendingTopups' in action && action.fetchPendingTopups) {
                yield* put(getPendingTopups());
                yield* put(getVRPConsents());
            }
        }
        if ('skipGetPositions' in action && !action.skipGetPositions) {
            for (const tradingAccount of tradingAccounts ?? []) {
                if (tradingAccount.account.status === 'ACTIVE') {
                    yield* put(getPositions({ accountId: tradingAccount.account.id, paging: { page: 1, perPage: 1000000 } }));
                }
            }
        }
        yield* put(updateWealthAccountsComplete());
    }
}
// Used to show the initial investment in MultiTradingAccountsView
function* fetchActivitiesForSingleSecclAccountWorker(action) {
    const activeSecclAccounts = action.payload.accounts.filter((account) => account.account.broker === 'seccl' && account.account.status === 'ACTIVE');
    if (activeSecclAccounts.length === 1) {
        yield* put(getActivities({ accountId: activeSecclAccounts[0].account.id, paging: { page: 1, perPage: 30 } }));
    }
}
function* updateWealthAccountsWithRetryWorker(action) {
    const isGBUser = yield* select(selectIsGBUser);
    if (isGBUser) {
        const { broker, accountType } = action;
        yield* all([putResolve(getTradingAccounts()), putResolve(getConnectedAccount()), putResolve(getPotsAccount())]);
        const isJISAFlow = accountType === 'JISA';
        const potsAccount = yield* select((state) => state.pots.account);
        const tradingAccounts = yield* select((state) => state.invest.accounts);
        const isPotsAccountActive = potsAccount?.status === 'ACTIVE';
        if (isJISAFlow) {
            const jisaTradingAccounts = tradingAccounts?.filter((account) => account.account.accountType === 'JISA');
            if ('retryTimes' in action && action.retryTimes) {
                const shouldRetry = !jisaTradingAccounts?.length ||
                    jisaTradingAccounts.some((account) => account.account.status === 'NOT_STARTED' ||
                        account.account.status === 'IDENTITY_CHECKS_IN_PROGRESS' ||
                        account.account.status === 'OPENING_IN_PROGRESS');
                if (shouldRetry) {
                    yield* delay(5 * 1000);
                    yield* put(updateWealthAccountsWithRetry(action.retryTimes - 1, broker, accountType));
                }
            }
        }
        else {
            const tradingAccount = tradingAccounts?.find((account) => account.account.broker === broker && account.account.accountType === accountType);
            const isTradingAccountActive = tradingAccount?.account?.status === 'ACTIVE';
            if (isTradingAccountActive || isPotsAccountActive) {
                if ('fetchPendingTopups' in action && action.fetchPendingTopups) {
                    yield* put(getPendingTopups());
                    yield* put(getVRPConsents());
                }
            }
            if ('retryTimes' in action && action.retryTimes) {
                if (tradingAccount?.account?.status === 'NOT_STARTED' ||
                    tradingAccount?.account?.status === 'IDENTITY_CHECKS_IN_PROGRESS' ||
                    tradingAccount?.account?.status === 'OPENING_IN_PROGRESS') {
                    yield* delay(5 * 1000);
                    yield* put(updateWealthAccountsWithRetry(action.retryTimes - 1, broker, accountType));
                }
            }
        }
    }
}
function* checkHmrcAppInstalledWorker() {
    const isGBUser = yield* select(selectIsGBUser);
    if ((!isIOS && !isAndroid) || !isGBUser) {
        return;
    }
    try {
        const isInstalled = isIOS
            ? yield* call(isAppInstalledIOS, HMRC_APP_URL_SCHEME)
            : yield* call(isAppInstalledAndroid, HMRC_APP_ANDROID_PACKAGE_NAME);
        yield* put(setHmrcAppInstalledAction(isInstalled));
    }
    catch (error) {
        Sentry.logError({ err: error, message: 'Failed to check if HMRC app is installed' });
    }
}
function* clearInvestOnboardingHistoryWorker() {
    yield* call(clearOnboardingHistory, 'invest');
}
function* investSaga() {
    log('[investSaga] starting...', false, 'cyan');
    yield* takeEvery(['CLEAR_ONBOARDING_STATE', 'PAUSE_ONBOARDING_STATE', LOGGED_OUT], clearInvestOnboardingHistoryWorker);
    yield* takeLatest(UPDATE_WEALTH_ACCOUNTS, updateWealthAccountsWorker);
    yield* takeLatest('UPDATE_WEALTH_ACCOUNTS_WITH_RETRY', updateWealthAccountsWithRetryWorker);
    yield* takeLatest('INVEST_GET_STOCK_ALERTS_SUCCESS_ACTION', fetchPricesForPriceAlertSymbols);
    yield* takeLatest('GET_TRADING_ACCOUNTS_SUCCESS', fetchActivitiesForSingleSecclAccountWorker);
    yield* takeEvery(REGISTER_USER_SUCCESS, updateWealthAccountsWorker);
    yield* takeEvery((action) => action.type === 'SET_REFERRER_ID', handleInvestInvite);
    yield* takeLatest((action) => [FETCH_APP_DATA, USER_RESUME_APP].includes(action.type), checkHmrcAppInstalledWorker);
    yield* spawn(autoinvestSaga);
    yield* spawn(topupSaga);
    yield* spawn(withdrawSagas);
    yield* spawn(transactionSagas);
}
export default investSaga;
